import { TemplateStudioModel } from 'common/contracts';
import {
  BodyBlockV2Item,
  DEFAULT_BODY_BLOCKS_ORDER,
  EFeatureFlag,
  MERGEABLE_BODY_BLOCKS,
  StoreBodyBlock
} from 'constants/enums';
import { TemplateStudioBlock } from 'state/template-studio.state.service';

import {
  bundleDesignsById,
  bundleDesignsByTheme
} from './DesignsMetadata/bundles.metadata';
import {
  buyOneGetManyDesignsById,
  buyOneGetManyDesignsByTheme
} from './DesignsMetadata/buyOneGetMany.metadata';
import {
  promotionDesignsById,
  promotionDesignsByTheme
} from './DesignsMetadata/promotions.metadata';
import {
  rollingOffersDesignsById,
  rollingOffersDesignsByTheme
} from './DesignsMetadata/rollingOffers.metadata';
import { EThemes } from './enums';

const NEW_DESIGN_THRESHOLD = 30 * 24 * 60 * 60 * 1000; // 30 days

export const isDesignNew = (designDate: string): boolean => {
  const createdAt = new Date(designDate).getTime();
  const daysSinceCreation = Date.now() - createdAt;
  return daysSinceCreation <= NEW_DESIGN_THRESHOLD;
};

const injectFeatureFlaggedBlocks = (
  baseOrder: StoreBodyBlock[],
  featureFlags: Record<string, boolean>
): StoreBodyBlock[] => {
  const order = [...baseOrder];

  // onePlusMany is intentionally omitted from DEFAULT_BODY_BLOCKS_ORDER so that seed/reset
  // paths never persist it when the flag is off. Inject it after rollingOffers when enabled.
  if (featureFlags[EFeatureFlag.DASHBOARD_ROLLING_OFFER_1N_ENABLED]) {
    const rollingOffersIndex = order.indexOf(StoreBodyBlock.rollingOffers);
    const insertAt =
      rollingOffersIndex === -1 ? order.length : rollingOffersIndex + 1;
    order.splice(insertAt, 0, StoreBodyBlock.onePlusMany);
  }

  return order;
};

export const getDefaultBodyBlocksOrder = (
  featureFlags: Record<string, boolean>
): StoreBodyBlock[] => {
  if (!featureFlags) return DEFAULT_BODY_BLOCKS_ORDER;

  const orderWithFlaggedBlocks = injectFeatureFlaggedBlocks(
    DEFAULT_BODY_BLOCKS_ORDER,
    featureFlags
  );

  const featureFlagMap: Partial<Record<StoreBodyBlock, boolean>> = {
    [StoreBodyBlock.progressBars]:
      featureFlags[EFeatureFlag.DASHBOARD_PROGRESS_BAR],
    [StoreBodyBlock.rollingOffers]:
      featureFlags[EFeatureFlag.DASHBOARD_ROLLING_OFFER_FEATURE],
    [StoreBodyBlock.dailyBonusBadge]:
      featureFlags[EFeatureFlag.DASHBOARD_DAILY_BONUS_BADGE],
    [StoreBodyBlock.playerLevel]:
      featureFlags[EFeatureFlag.DASHBOARD_BODYBLOCK_SUPPORT_PLAYER_LEVEL],
    [StoreBodyBlock.rewardCalendar]:
      featureFlags[EFeatureFlag.DASHBOARD_REWARD_CALENDAR],
    [StoreBodyBlock.triggeredPopups]:
      featureFlags[EFeatureFlag.DASHBOARD_TRIGGERED_POPUPS]
  };

  return orderWithFlaggedBlocks.filter(
    (block: StoreBodyBlock) => featureFlagMap[block] !== false
  );
};

export const buildBodyBlockItemId = (blocks: StoreBodyBlock[]): string =>
  `bb-${blocks.map(String).join('+')}`;

export const getDefaultBodyBlocksOrderV2 = (
  defaultBodyBlocksOrder: StoreBodyBlock[]
): BodyBlockV2Item[] =>
  defaultBodyBlocksOrder.map((block) => ({
    id: buildBodyBlockItemId([block]),
    blocks: [block],
    isVisible: true,
    isMergeable: MERGEABLE_BODY_BLOCKS.has(block)
  }));

export const flattenBodyBlocksV2 = (
  bodyBlocks: BodyBlockV2Item[]
): StoreBodyBlock[] =>
  bodyBlocks
    .filter(({ isVisible }) => isVisible)
    .flatMap(({ blocks }) => blocks);

export { revealBodyBlockInV2Order } from './bodyBlocksOrderV2.utils';

export const checkNewDesignByBlock: Partial<Record<TemplateStudioBlock, any>> =
  {
    [StoreBodyBlock.bundles]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedBundleDesignTheme: EThemes | undefined =
        bundleDesignsById[selectedDesignId]?.theme;

      if (!selectedBundleDesignTheme) return false;

      const hasNewBundleDesigns = bundleDesignsByTheme[
        selectedBundleDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewBundleDesigns;
    },
    [StoreBodyBlock.specialOffers]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedPromotionDesignTheme: EThemes | undefined =
        promotionDesignsById[selectedDesignId]?.theme;

      const hasNewPromotionDesigns = promotionDesignsByTheme[
        selectedPromotionDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewPromotionDesigns;
    },
    [StoreBodyBlock.rollingOffers]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedRollingOffersDesignTheme: EThemes | undefined =
        rollingOffersDesignsById[selectedDesignId]?.theme;

      const hasNewRollingOffersDesigns = rollingOffersDesignsByTheme[
        selectedRollingOffersDesignTheme as EThemes
      ].some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );

      return hasNewRollingOffersDesigns;
    },
    [StoreBodyBlock.onePlusMany]: (selectedDesignId: string) => {
      if (!selectedDesignId) return false;

      const selectedBuyOneGetManyDesignTheme: EThemes | undefined =
        buyOneGetManyDesignsById[selectedDesignId]?.theme;

      if (selectedBuyOneGetManyDesignTheme === undefined) return false;

      const designsInTheme =
        buyOneGetManyDesignsByTheme[selectedBuyOneGetManyDesignTheme];

      if (designsInTheme === undefined) return false;

      return designsInTheme.some(
        ({ id, createdAt }) => id !== selectedDesignId && isDesignNew(createdAt)
      );
    }
  };

export const getOutdatedBlocks = ({
  bundle,
  promotion,
  rollingOffers,
  buyOneGetMany
}: TemplateStudioModel & { specificBlockToCheck?: TemplateStudioBlock }) => {
  const outdatedBlocks = new Set<TemplateStudioBlock>();

  if (
    checkNewDesignByBlock[StoreBodyBlock.bundles]!(bundle?.selectedDesignId)
  ) {
    outdatedBlocks.add(StoreBodyBlock.bundles);
  }

  if (
    checkNewDesignByBlock[StoreBodyBlock.specialOffers]!(
      promotion?.selectedDesignId
    )
  ) {
    outdatedBlocks.add(StoreBodyBlock.specialOffers);
  }

  if (
    checkNewDesignByBlock[StoreBodyBlock.rollingOffers]!(
      rollingOffers?.selectedDesignId
    )
  ) {
    outdatedBlocks.add(StoreBodyBlock.rollingOffers);
  }

  if (
    buyOneGetMany &&
    checkNewDesignByBlock[StoreBodyBlock.onePlusMany]!(
      buyOneGetMany.selectedDesignId
    )
  ) {
    outdatedBlocks.add(StoreBodyBlock.onePlusMany);
  }

  return outdatedBlocks;
};
